import React, { Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFormikContext } from "formik";

import { LatLngLiteral } from "leaflet";

import { HvGrid, HvInput, HvLoading } from "@hitachivantara/uikit-react-core";

import { geocodeLatLng, geocodeAddress, IconData } from "lib/api";

import Loading from "components/layout/Loading";
import GenericMap from "components/common/GenericMap";

import useStyles from "./styles";

export interface LocationFormData {
  address?: string;
  latitude?: number;
  longitude?: number;
}

interface LocationProps {
  marker?: IconData;
}

const Location: React.FC<LocationProps> = ({ marker }) => {
  const { t } = useTranslation("selectLocationDialog");
  const classes = useStyles();

  const [location, setLocation] = useState<LatLngLiteral | undefined>(
    undefined
  );

  const { values, setValues, setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<LocationFormData>();

  const parseStatus = (field: keyof LocationFormData) => {
    return errors[field] && touched[field] ? "invalid" : "valid";
  };

  const parseStatusMessage = (field: keyof LocationFormData) => {
    return errors[field] ? errors[field] : "";
  };

  const onChange = useCallback(
    async (newLoc: LatLngLiteral) => {
      setValues({
        latitude: newLoc.lat,
        longitude: newLoc.lng,
      });
    },
    [setValues]
  );

  const onSearch = useCallback(
    async (search: string) => {
      const geocode = await geocodeAddress(search);
      setValues({
        latitude: geocode?.data.coordinates.lat,
        longitude: geocode?.data.coordinates.lng,
      });
    },
    [setValues]
  );

  const latitudeInvalid = (errors.latitude ?? "") !== "";
  const longitudeInvalid = (errors.longitude ?? "") !== "";

  useEffect(() => {
    const lat = Number(values.latitude);
    const lng = Number(values.longitude);
    if (
      (values.latitude as unknown) !== "" &&
      !Number.isNaN(lat) &&
      (values.longitude as unknown) !== "" &&
      !Number.isNaN(lng)
    ) {
      setLocation({
        lat,
        lng,
      });
    } else {
      setLocation(undefined);
    }
  }, [values.latitude, values.longitude]);

  const [loadingAddress, setLoadingAddress] = useState(false);
  useEffect(() => {
    const getAddress = async () => {
      try {
        const geocode = await geocodeLatLng(location);

        setFieldValue("address", geocode?.data.formattedAddress ?? "");
      } catch (e) {
        setFieldValue("address", "");
      }

      setLoadingAddress(false);
    };

    if (location != null) {
      setLoadingAddress(true);
      getAddress();
    } else {
      setFieldValue("address", "");
    }
  }, [location, setFieldValue]);

  return (
    <Suspense
      fallback={
        <Loading className={classes.loading} delay={1000} loadingLabel="" />
      }
    >
      <HvGrid container>
        <HvGrid xs={6} item>
          <GenericMap
            clickToMoveMarker
            dragToMoveMarker
            onChange={onChange}
            onSearch={onSearch}
            marker={marker}
            location={location}
            searchPlaceholder={t("map.searchPlaceholder")}
            searchButtonLabel={t("map.searchButtonLabel")}
            clearButtonLabel={t("map.clearButtonLabel")}
          />
        </HvGrid>
        <HvGrid container item xs={6} alignContent="flex-start">
          <HvGrid xs={12} item>
            <HvInput
              className={classes.input}
              label={t("fields.address")}
              readOnly
              placeholder={loadingAddress ? "" : "—"}
              value={
                loadingAddress || latitudeInvalid || longitudeInvalid
                  ? ""
                  : values.address ?? ""
              }
              status={
                loadingAddress || latitudeInvalid || longitudeInvalid
                  ? "valid"
                  : parseStatus("address")
              }
              statusMessage={parseStatusMessage("address")}
              endAdornment={loadingAddress && <HvLoading small />}
            />
          </HvGrid>
          <HvGrid xs={12} md={6} item>
            <HvInput
              className={classes.input}
              type="number"
              label={t("fields.latitude")}
              placeholder={t("fields.latitudePlaceholder")}
              inputProps={{
                autoComplete: "off",
              }}
              value={values.latitude?.toString() ?? ""}
              required
              status={parseStatus("latitude")}
              statusMessage={parseStatusMessage("latitude")}
              onChange={(evt, value) => {
                setFieldValue("latitude", value);
              }}
              onBlur={() => {
                setFieldTouched("latitude");
              }}
            />
          </HvGrid>
          <HvGrid xs={12} md={6} item>
            <HvInput
              className={classes.input}
              type="number"
              label={t("fields.longitude")}
              placeholder={t("fields.longitudePlaceholder")}
              value={values.longitude?.toString() ?? ""}
              required
              status={parseStatus("longitude")}
              statusMessage={parseStatusMessage("longitude")}
              onChange={(evt, value) => {
                setFieldValue("longitude", value);
              }}
              onBlur={() => {
                setFieldTouched("longitude");
              }}
            />
          </HvGrid>
        </HvGrid>
      </HvGrid>
    </Suspense>
  );
};

export default Location;
